<template>
    <div class="relative max-w-7xl mx-auto pb-20 px-4">

        <!-- Upload Interface -->
        <div v-if="!selectedFile" class="relative max-w-4xl mx-auto bg-white rounded-[1rem] shadow-[0_30px_80px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden transition-all duration-500">
            <div class="absolute inset-0 opacity-[0.03] pointer-events-none" style="background-image: radial-gradient(#f43f5e 0.5px, transparent 0.5px); background-size: 24px 24px;"></div>
            
            <div class="relative p-6 sm:p-6">
                <!-- Primary Interface -->
                <div class="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    <div>
                        <!-- URL Input Section (Premium Design) -->
                        <div class="relative group/url mb-1">
                            <div class="flex items-center gap-1 p-0.5 bg-white border-2 border-gray-100 ring-1 ring-logo-blue/40 rounded-[12px] shadow-sm focus-within:border-2 focus-within:border-logo-blue/60 focus-within:ring-4 focus-within:ring-logo-blue/10 transition-all duration-500">
                                <div class="pl-3">
                                    <div>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-youtube size-5 shrink-0 text-primary opacity-80" aria-hidden="true"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"></path><path d="m10 15 5-3-5-3z"></path></svg>
                                    </div>
                                </div>
                                <input 
                                    v-model="videoUrl"
                                    type="text" 
                                    placeholder="Paste YouTube, Reels, or Video URL here..." 
                                    class="flex-1 bg-transparent border-none focus:ring-0 text-gray-900 placeholder:text-gray-400 py-2.5 text-base font-medium"
                                    @keyup.enter="processVideoUrl"
                                >
                                <button 
                                    @click="processVideoUrl"
                                    :disabled="!videoUrl || isProcessing"
                                    class="relative overflow-hidden bg-logo-blue hover:bg-[#6B0B2D] text-white px-6 py-2.5 mr-0.5 rounded-[12px] font-semibold text-sm shadow-lg shadow-logo-blue/20 hover:shadow-logo-blue/30 transition-all duration-300 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed whitespace-nowrap group/btn"
                                >
                                    <span class="relative z-10">Analyze Video</span>
                                    <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover/btn:animate-[shimmer_1.5s_infinite]"></div>
                                </button>
                            </div>
                        </div>

                        <!-- OR Separator with Premium Lines -->
                        <div class="relative py-2 mb-2">
                            <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                <div class="w-full border-t border-gray-100"></div>
                            </div>
                            <div class="relative flex justify-center">
                                <span class="bg-white px-3 text-[13px] font-semibold text-gray-400 uppercase tracking-[0.3em] border border-gray-50 rounded-full py-1">OR</span>
                            </div>
                        </div>

                        <!-- Dynamic Upload Zone (Existing) -->
                        <div 
                            @click="$refs.fileInput.click()"
                            @dragover.prevent="dragActive = true"
                            @dragleave.prevent="dragActive = false"
                            @drop.prevent="handleDrop"
                            :class="dragActive ? 'border-logo-blue bg-logo-pale scale-[0.99] ring-8 ring-logo-blue/10' : 'border-gray-100 bg-gray-50/50 hover:border-logo-blue/40 hover:bg-white'"
                            class="relative border-2 border-dashed rounded-[1rem] py-12 text-center cursor-pointer transition-all duration-500 group overflow-hidden"
                        >
                            <input type="file" ref="fileInput" class="hidden" accept="video/*" @change="handleFileSelect">
                            
                            <div class="flex flex-col items-center">
                                <!-- Upload Icon Box -->
                                <div class="w-14 h-14 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center mb-6 shadow-2xl group-hover:border-logo-blue/30 group-hover:shadow-2xl group-hover:shadow-logo-blue/10 transition-all duration-500">
                                    <Upload class="w-6 h-6 text-black group-hover:text-logo-blue transition-colors duration-500" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 mb-3 group-hover:text-logo-blue transition-colors duration-500">Drag &amp; Drop or Click to upload video here</h3>
                                <div class="flex items-center gap-2 text-gray-500 text-xs font-medium tracking-wide">
                                    <span>Max 150MB</span>
                                    <div class="w-1.5 h-1.5 bg-gray-300 mt-0.5 rounded-full"></div>
                                    <span>MP4 / MOV / AVI / WEBM / MKV</span>
                                </div>
                            </div>
                        </div>

                        <!-- Trust Badges (Matching Image) -->
                        <div class="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 pt-6">
                            <div class="flex items-center gap-3">
                                <CheckCircle class="w-5 h-5 text-logo-blue" />
                                <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-500">No account required</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <CheckCircle class="w-5 h-5 text-logo-blue" />
                                <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-500">No credit card</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <CheckCircle class="w-5 h-5 text-logo-blue" />
                                <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Free to use</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Processing State -->
        <div v-else-if="isProcessing" class="max-w-4xl mx-auto space-y-8 py-10 animate-in zoom-in-95 duration-500">
            <div class="relative p-10 bg-white rounded-[1.5rem] border border-gray-100 shadow-[0_30px_80px_rgba(0,0,0,0.04)] overflow-hidden text-center">
                <div class="flex justify-center gap-2 mb-6">
                    <div v-for="i in 3" :key="i" class="w-2.5 h-2.5 bg-logo-blue rounded-full animate-bounce" :style="`animation-delay: ${i * 0.2}s`"></div>
                </div>
                
                <h4 class="text-xl font-semibold text-gray-800 mb-2">Analyzing Video Stream</h4>
                <p class="text-gray-500 text-sm font-medium mb-8">
                    {{ selectedFile.type === 'video/url' ? 'Downloading and extracting frames from remote link...' : 'Extracting high-resolution frames locally in the browser...' }}
                </p>
                
                <div class="max-w-xs mx-auto space-y-3">
                    <div class="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200/50">
                        <div 
                            class="h-full bg-logo-blue rounded-full transition-all duration-500 ease-out"
                            :style="{ width: progress + '%' }"
                        ></div>
                    </div>
                    <div class="flex justify-between text-[10px] font-bold text-logo-blue uppercase tracking-[0.2em]">
                        <span>Extracting</span>
                        <span>{{ Math.round(progress) }}%</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Workspace (After Extraction) -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start animate-in fade-in duration-500">
            
            <!-- Left Panel: Video Player & Extraction Settings (5 Columns) -->
            <div class="lg:col-span-4 space-y-3">
                
                <!-- Video Section -->
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 space-y-3 max-w-[92%] mx-auto">
                    <div v-if="selectedFile" class="px-1 py-0.5 border-b border-gray-50 flex items-center justify-between">
                        <span class="text-xs font-bold text-gray-800 truncate max-w-[200px]" :title="selectedFile.name">
                            {{ selectedFile.name }}
                        </span>
                        <span v-if="selectedFile.type === 'video/url'" class="px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded text-[9px] font-bold uppercase tracking-wider">
                            YouTube URL
                        </span>
                        <span v-else class="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-bold uppercase tracking-wider">
                            Local Video
                        </span>
                    </div>
                    <div class="relative rounded-xl overflow-hidden bg-black aspect-video flex items-center justify-center">
                        <iframe 
                            v-if="youtubeEmbedUrl"
                            :src="youtubeEmbedUrl"
                            class="w-full h-full max-h-[350px] aspect-video rounded-xl"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                        <video 
                            v-else
                            ref="videoPlayer" 
                            :src="localVideoUrl" 
                            controls 
                            crossorigin="anonymous"
                            class="w-full h-full max-h-[350px]"
                            @timeupdate="onVideoTimeUpdate"
                            @loadedmetadata="onVideoLoadedMetadata"
                        ></video>
                    </div>

                    <!-- Custom Timeline -->
                    <div class="space-y-1 max-w-[90%] mx-auto">
                        <div class="flex justify-between text-xs font-medium text-gray-500 px-1">
                            <span>Video Timeline</span>
                            <span class="text-gray-500 text-xs font-medium">{{ formatTime(videoCurrentTime) }} / {{ formatTime(duration) }}</span>
                        </div>
                        <div 
                            ref="timelineBar"
                            @click="seekVideoFromTimeline"
                            class="relative h-5 bg-gray-100 hover:bg-gray-200/85 rounded-lg cursor-pointer select-none border border-gray-200/50 transition-colors"
                        >
                            <!-- Frame Markers (Green) -->
                            <div 
                                v-for="(frame, idx) in frames" 
                                :key="frame.id"
                                class="absolute top-0 bottom-0 w-[3px] bg-emerald-500 transition-all cursor-pointer hover:bg-emerald-600 hover:w-[3.5px] group"
                                :style="{ left: getPercentage(frame.timestamp) + '%' }"
                                @click.stop="seekToFrame(frame.timestamp)"
                                :title="`Captured at ${formatTime(frame.timestamp)}`"
                            >
                                <div class="absolute -top-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-emerald-500 rotate-45 border border-white opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </div>
                            
                            <!-- Playhead (Orange) -->
                            <div 
                                class="absolute top-0 bottom-0 w-[4px] bg-orange-400 pointer-events-none"
                                :style="{ left: getPercentage(videoCurrentTime) + '%' }">
                                <div class="absolute -top-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-orange-400 rotate-45 border border-white shadow-sm"></div>
                            </div>
                        </div>
                        <div class="flex justify-between px-1 text-[9px] text-gray-500 font-semibold tracking-wider uppercase mt-1">
                            <span v-for="tick in timelineTicks" :key="tick">{{ formatTime(tick) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Sampling Settings -->
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden max-w-[92%] mx-auto">
                    <div class="px-5 py-2 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
                     <label class="text-xs font-bold text-gray-400 uppercase tracking-wider">Interval Settings</label>
                    </div>
                    <div class="p-3 space-y-2">
                        <div class="space-y-3">
                            <div class="grid grid-cols-4 gap-3 max-w-[88%] mx-auto">
                                <button 
                                    v-for="preset in [1, 5, 10]" 
                                    :key="preset"
                                    @click="selectPreset(preset)"
                                    :class="(!showCustomInterval && samplingInterval === preset) ? 'bg-logo-blue text-white shadow-md shadow-logo-blue/20' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'"
                                    class="py-1.5 text-xs font-bold rounded-lg transition-all"
                                >
                                    {{ preset }}s
                                </button>
                                <button 
                                    @click="toggleCustom"
                                    :class="showCustomInterval ? 'bg-logo-blue text-white shadow-md shadow-logo-blue/20' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'"
                                    class="py-1.5 text-xs font-bold rounded-lg transition-all"
                                >
                                    Custom
                                </button>
                            </div>

                            <!-- Custom Interval range + Apply button in 1 line -->
                            <div v-if="showCustomInterval" class="pt-1.5 flex items-center gap-3 animate-in slide-in-from-top-2 duration-300">
                                <div class="flex-1 flex items-center gap-2">
                                    <input 
                                        type="range" 
                                        v-model.number="samplingInterval" 
                                        min="1" 
                                        max="60" 
                                        class="flex-1 custom-range-slider"
                                    >
                                    <span class="text-xs font-bold text-logo-blue whitespace-nowrap min-w-[28px] text-right">{{ samplingInterval }}s</span>
                                </div>
                                <button 
                                    @click="resampleVideo"
                                    class="px-3.5 py-1.5 bg-logo-blue hover:bg-[#6B0B2D] text-white text-xs font-bold uppercase tracking-wider rounded-lg shadow-md transition-all flex items-center gap-1 whitespace-nowrap"
                                >
                                    <Zap class="w-3.5 h-3.5 fill-current" />
                                    Apply
                                </button>
                            </div>
                        </div>

                        <!-- Action Buttons Grid -->
                        <div class="pt-2 border-t border-gray-100 grid grid-cols-2 gap-3 max-w-[90%] mx-auto">
                            <button 
                                @click="captureCurrentFrame"
                                :disabled="selectedFile && selectedFile.type === 'video/url'"
                                :class="selectedFile && selectedFile.type === 'video/url' ? 'opacity-40 cursor-not-allowed bg-gray-400 text-gray-200' : 'bg-gray-900 hover:bg-black text-white'"
                                class="w-full py-2 text-xs font-semibold uppercase tracking-wider rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5">
                                <Camera class="w-3 h-3" />
                                Capture Frame
                            </button>
                            <button 
                                @click="resetWorkspace"
                                class="w-full py-2 bg-red-50 hover:bg-red-500 hover:text-white border border-red-50 hover:border-red-500 rounded-xl text-red-600 text-xs font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5">
                                <RefreshCw class="w-3 h-3" />
                                New File
                            </button>
                        </div>

                        <!-- Instantly Capture Helper Tip -->
                        <p v-if="selectedFile && selectedFile.type === 'video/url'" class="text-[9px] text-rose-500 font-semibold text-center mt-1">
                            Note: Manual capture is disabled for YouTube videos.
                        </p>
                        <p v-else class="text-[10px] text-gray-400 font-semibold text-center mt-1">
                            Tip: Press <kbd class="px-1.5 py-0.5 bg-gray-50 border rounded text-gray-500 font-mono shadow-sm">C</kbd> to capture frame instantly.
                        </p>
                    </div>
                </div>
                </div>

            <!-- Right Panel: Grid of Slides & Export Actions (7 Columns) -->
            <div class="lg:col-span-8 space-y-3">
                
                <!-- Action Bar -->
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 flex flex-wrap justify-between items-center gap-4">
                    <div class="flex items-center gap-3">
                        <input 
                            type="checkbox" 
                            :checked="isAllSelected" 
                            @change="toggleSelectAll"
                            class="w-5 h-5 rounded text-logo-blue border-gray-300 focus:ring-logo-blue"
                            id="selectAll"
                        >
                        <label for="selectAll" class="text-sm font-bold text-gray-800 cursor-pointer">
                            Select All ({{ selectedCount }}/{{ frames.length }})
                        </label>
                    </div>
                    
                    <div class="flex items-center gap-3 relative">
                        <!-- Share button -->
                        <button 
                            @click="shareDocument" 
                            class="px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 rounded-xl font-bold text-xs uppercase tracking-wider transition flex items-center gap-2"
                        >
                            <Share2 class="w-4 h-4" />
                            Share
                        </button>
                        
                        <!-- Compile/Download PDF dropdown wrapper -->
                        <div class="relative">
                            <button 
                                @click="showDownloadMenu = !showDownloadMenu"
                                class="px-5 py-2.5 bg-emerald-500 text-white hover:bg-emerald-600 rounded-xl font-bold text-xs uppercase tracking-wider transition flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                            >
                                <Download class="w-4 h-4" />
                                Download PDF
                                <ChevronDown class="w-4 h-4" />
                            </button>

                            <!-- PDF Config Dropdown Menu -->
                            <div v-if="showDownloadMenu" class="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-gray-100 shadow-2xl p-5 z-[150] space-y-4 animate-in slide-in-from-top-4 duration-300 text-left">
                                <h4 class="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">PDF Export Settings</h4>
                                
                                <div class="space-y-3">
                                    <!-- Document Title -->
                                    <div class="space-y-1">
                                        <label class="text-[10px] font-bold text-gray-400 uppercase">Document Title</label>
                                        <input 
                                            v-model="pdfSettings.title" 
                                            type="text" 
                                            class="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-logo-blue focus:ring-1 focus:ring-logo-blue"
                                            placeholder="Enter PDF title..."
                                        >
                                    </div>
                                    
                                    <!-- Layout Option -->
                                    <div class="space-y-1">
                                        <label class="text-[10px] font-bold text-gray-400 uppercase">Layout Style</label>
                                        <select 
                                            v-model="pdfSettings.layout" 
                                            class="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-logo-blue focus:ring-1 focus:ring-logo-blue"
                                        >
                                            <option value="original">(Recommended) Original Aspect Ratio</option>
                                            <option value="landscape">Landscape (1 frame / page)</option>
                                            <option value="portrait_grid">Portrait Grid (2 frames / page)</option>
                                            <option value="portrait_notes">Portrait with Notes (1 frame + Lines)</option>
                                        </select>
                                    </div>

                                    <!-- Cover Page Toggle -->
                                    <div class="flex items-center justify-between py-1">
                                        <span class="text-xs font-bold text-gray-600">Include Cover Page</span>
                                        <input 
                                            type="checkbox" 
                                            v-model="pdfSettings.coverPage"
                                            class="w-4 h-4 rounded text-logo-blue border-gray-300 focus:ring-logo-blue"
                                        >
                                    </div>

                                    <!-- Timestamps Toggle -->
                                    <div class="flex items-center justify-between py-1">
                                        <span class="text-xs font-bold text-gray-600">Draw Timestamps</span>
                                        <input 
                                            type="checkbox" 
                                            v-model="pdfSettings.timestamps"
                                            class="w-4 h-4 rounded text-logo-blue border-gray-300 focus:ring-logo-blue"
                                        >
                                    </div>

                                    <!-- Page Numbers Toggle -->
                                    <div class="flex items-center justify-between py-1">
                                        <span class="text-xs font-bold text-gray-600">Draw Page Numbers</span>
                                        <input 
                                            type="checkbox" 
                                            v-model="pdfSettings.pageNumbers"
                                            class="w-4 h-4 rounded text-logo-blue border-gray-300 focus:ring-logo-blue"
                                        >
                                    </div>
                                </div>

                                <button 
                                    @click="compileAndDownloadPdf"
                                    class="w-full py-2.5 bg-logo-blue hover:bg-[#6B0B2D] text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-md transition-colors flex items-center justify-center gap-2"
                                >
                                    <FileText class="w-4 h-4" />
                                    Generate &amp; Download
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- Scrollable Slides Area -->
                <div class="max-h-[347px] overflow-y-auto border border-gray-100 rounded-2xl bg-gray-50/50 p-3 shadow-inner">
                    
                    <!-- Slide List Grid -->
                    <div v-if="frames.length > 0" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        <div 
                            v-for="(frame, idx) in frames" 
                            :key="frame.id"
                            :class="frame.selected ? 'border-emerald-400 ring-2 ring-emerald-500/10' : 'border-gray-200 opacity-70 hover:opacity-100'"
                            class="bg-white border rounded-xl overflow-hidden shadow-sm relative transition-all duration-300 group/card"
                        >
                            <!-- Top Action Row -->
                            <div class="absolute top-2 inset-x-2 z-10 flex justify-between items-center">
                                <!-- Toggle Selection Checkbox -->
                                <input 
                                    type="checkbox" 
                                    v-model="frame.selected"
                                    class="w-4 h-4 rounded text-emerald-500 border-gray-300 focus:ring-emerald-500 shadow-sm cursor-pointer"
                                >
                                
                                <!-- Frame Modifiers -->
                                <div class="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 bg-white/95 backdrop-blur p-1 rounded-lg shadow-sm border border-gray-100">
                                    <button 
                                        @click="startCropping(frame)" 
                                        class="p-1 hover:bg-gray-100 text-gray-600 hover:text-logo-blue rounded transition"
                                        title="Crop frame"
                                    >
                                        <Crop class="w-3.5 h-3.5" />
                                    </button>
                                    <button 
                                        @click="deleteFrame(frame.id)" 
                                        class="p-1 hover:bg-red-50 text-gray-600 hover:text-red-500 rounded transition"
                                        title="Delete frame"
                                    >
                                        <Trash2 class="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>

                            <!-- Image Frame Preview -->
                            <div class="aspect-video bg-gray-50 flex items-center justify-center overflow-hidden border-b border-gray-100">
                                <img :src="frame.image" class="w-full h-full object-contain">
                            </div>

                            <!-- Footer Badge Metadata -->
                            <div class="px-2.5 py-2 bg-gray-50/50 flex justify-between items-center">
                                <!-- Timestamp -->
                                <div class="flex items-center gap-1 text-[10px] text-gray-500 font-bold bg-white border border-gray-200/50 px-1.5 py-0.5 rounded-md">
                                    <Clock class="w-3 h-3 text-logo-blue" />
                                    {{ formatTime(frame.timestamp) }}
                                </div>

                                <!-- Page Index -->
                                <div class="text-[9px] font-black uppercase text-gray-400 bg-white border border-gray-200/50 px-1.5 py-0.5 rounded-md">
                                    Page {{ idx + 1 }}
                                </div>
                            </div>

                        </div>

                        <!-- CTA Cards (Upsell / Upgrade Now - Mockup style) -->
                        <div class="bg-white border border-logo-light/80 rounded-xl overflow-hidden shadow-sm relative transition-all duration-300 group/card flex flex-col">
                            <!-- Body matching aspect-video -->
                            <div class="aspect-video bg-gradient-to-br from-logo-pale to-logo-light/20 p-2.5 flex flex-col justify-center relative overflow-hidden border-b border-gray-100">
                                <div class="absolute -right-0 -top-0 opacity-[0.08] group-hover:scale-110 transition-transform duration-700">
                                    <FileText class="w-10 h-10 text-logo-blue" />
                                </div>
                                <div class="z-10">
                                    <span class="px-1.5 py-0.5 bg-logo-blue/15 text-logo-blue text-[7px] font-bold uppercase tracking-wider rounded-md">Limit Notice</span>
                                    <h5 class="text-[11px] font-bold text-gray-900 mt-1 leading-tight">First 10 minutes only</h5>
                                    <p class="text-[9px] font-semibold text-gray-500 mt-1 leading-snug">Upgrade to unlock unlimited length processing.</p>
                                </div>
                            </div>
                            
                            <!-- Footer matching slide footer -->
                            <div class="px-2.5 py-2 bg-gray-50/50 flex items-center justify-center">
                                <button class="w-full py-1 bg-logo-blue hover:bg-[#6B0B2D] text-white text-[9px] font-semibold uppercase tracking-wider rounded-md shadow-md shadow-logo-blue/10 transition-all">
                                    Upgrade Now
                                </button>
                            </div>
                        </div>

                    </div>
                    
                    <!-- Empty State -->
                    <div v-else class="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl py-20 text-center">
                        <p class="text-gray-500 font-semibold text-md">No frames captured. Resample or capture a frame manually above.</p>
                    </div>

                </div>
            </div>
        </div>

        <!-- Crop Modal -->
        <div v-if="croppingFrame" class="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div class="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col">
                <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 class="text-base font-bold text-gray-900 flex items-center gap-2">
                        <Crop class="w-5 h-5 text-logo-blue" />
                        Crop Captured Frame
                    </h3>
                    <button @click="closeCropper" class="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                        <X class="w-6 h-6" />
                    </button>
                </div>
                <div class="p-6 flex-1 flex items-center justify-center bg-gray-50 max-h-[60vh]">
                    <div class="max-w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-black">
                        <img ref="cropperImage" :src="croppingFrame.image" class="max-w-full max-h-[45vh] block">
                    </div>
                </div>
                <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                    <span class="text-xs font-medium text-gray-500">Drag bounding box to crop the frame</span>
                    <div class="flex gap-3">
                        <button @click="closeCropper" class="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-200 rounded-xl transition">
                            Cancel
                        </button>
                        <button @click="applyCrop" class="px-5 py-2 text-xs font-bold uppercase tracking-wider text-white bg-logo-blue hover:bg-[#6B0B2D] rounded-xl shadow-lg transition">
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Custom Notification Alert -->
        <div v-if="alertMessage" class="fixed bottom-5 right-5 z-[300] animate-in slide-in-from-bottom-5 duration-300">
            <div class="bg-gray-900 border border-gray-800 text-white px-5 py-4 rounded-xl shadow-2xl flex items-center gap-3">
                <Info class="w-5 h-5 text-logo-cyan animate-pulse" />
                <span class="text-xs font-bold tracking-wide uppercase">{{ alertMessage }}</span>
            </div>
        </div>

        <!-- Error Alert -->
        <div v-if="error" class="max-w-4xl mx-auto mt-6 animate-in slide-in-from-top-4">
            <div class="bg-red-50 text-red-600 p-5 rounded-2xl flex items-center gap-4 text-xs font-bold border border-red-100 shadow-sm">
                <AlertCircle class="w-6 h-6 shrink-0" />
                <span>{{ error }}</span>
                <button @click="error = null" class="ml-auto p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                    <X class="w-4.5 h-4.5" />
                </button>
            </div>
        </div>

    </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

import { 
    Upload, CheckCircle, Trash2, Crop, Download, 
    Share2, X, AlertCircle, RefreshCw, ChevronDown, 
    Clock, Camera, FileText, Info, Zap
} from 'lucide-vue-next';

export default {
    name: 'VideoToPdfComponent',
    components: {
        Upload, CheckCircle, Trash2, Crop, Download, 
        Share2, X, AlertCircle, RefreshCw, ChevronDown, 
        Clock, Camera, FileText, Info, Zap
    },
    setup() {
        // --- States ---
        const videoUrl = ref('');
        const selectedFile = ref(null);
        const localVideoUrl = ref(null);
        const duration = ref(0);
        const videoCurrentTime = ref(0);
        const isProcessing = ref(false);
        const progress = ref(0);
        const dragActive = ref(false);
        const error = ref(null);
        const alertMessage = ref(null);

        // Core Workspace list of frames
        // Each frame: { id, timestamp, image: base64, selected: true }
        const frames = ref([]);

        // Settings / Controls
        const samplingMode = ref('fixed'); // 'fixed' or 'dedup'
        const samplingInterval = ref(5); // in seconds
        const showCustomInterval = ref(false);
        const showDownloadMenu = ref(false);

        // Cropper
        const croppingFrame = ref(null);
        const cropperImage = ref(null);
        let cropperInstance = null;

        // PDF Export config
        const pdfSettings = ref({
            title: 'Video Lecture Slides',
            layout: 'original', // original, landscape, portrait_grid, portrait_notes
            coverPage: false,
            timestamps: false,
            pageNumbers: false
        });

        // HTML elements
        const videoPlayer = ref(null);
        const timelineBar = ref(null);

        // --- Computeds ---
        const youtubeEmbedUrl = computed(() => {
            if (selectedFile.value && selectedFile.value.type === 'video/url' && videoUrl.value) {
                const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                const match = videoUrl.value.match(regExp);
                if (match && match[2].length === 11) {
                    return `https://www.youtube.com/embed/${match[2]}`;
                }
            }
            return null;
        });

        const isAllSelected = computed(() => {
            return frames.value.length > 0 && frames.value.every(f => f.selected);
        });

        const selectedCount = computed(() => {
            return frames.value.filter(f => f.selected).length;
        });

        const timelineTicks = computed(() => {
            if (!duration.value) return [0];
            const ticks = [];
            const count = 6;
            const interval = duration.value / (count - 1);
            for (let i = 0; i < count; i++) {
                ticks.push(i * interval);
            }
            return ticks;
        });

        // --- Helper formatting ---
        const formatTime = (seconds) => {
            if (isNaN(seconds) || seconds === null) return '00:00';
            const m = Math.floor(seconds / 60);
            const s = Math.floor(seconds % 60);
            return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        };

        const getPercentage = (time) => {
            if (!duration.value) return 0;
            return (time / duration.value) * 100;
        };

        // --- File Select Handling ---
        const handleFileSelect = (e) => {
            const file = e.target.files[0];
            if (file) validateAndSetFile(file);
        };

        const handleDrop = (e) => {
            dragActive.value = false;
            const file = e.dataTransfer.files[0];
            if (file) validateAndSetFile(file);
        };

        const validateAndSetFile = (file) => {
            if (!file.type.startsWith('video/')) {
                error.value = "Format not supported. Please select a valid video file.";
                return;
            }
            if (file.size > 150 * 1024 * 1024) {
                error.value = "Local file exceeds the 150MB limit.";
                return;
            }
            selectedFile.value = file;
            if (localVideoUrl.value) URL.revokeObjectURL(localVideoUrl.value);
            localVideoUrl.value = URL.createObjectURL(file);
            error.value = null;
            frames.value = [];
            
            // Automatically launch extraction loop
            nextTick(() => {
                runLocalExtraction();
            });
        };

        // --- Local Frame Extraction ---
        const captureFrameAtTime = (video, time) => {
            return new Promise((resolve) => {
                const onSeeked = () => {
                    video.removeEventListener('seeked', onSeeked);
                    const canvas = document.createElement('canvas');
                    // Downscale frames slightly for responsive loading & PDF sizing (720p target width)
                    const scale = Math.min(1.0, 1280 / video.videoWidth);
                    canvas.width = video.videoWidth * scale;
                    canvas.height = video.videoHeight * scale;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                video.addEventListener('seeked', onSeeked);
                video.currentTime = time;
            });
        };

        const compareFrames = (currentDataUrl, prevDataUrl) => {
            if (!prevDataUrl) return Promise.resolve(true);
            return new Promise((resolve) => {
                const img1 = new Image();
                const img2 = new Image();
                let loaded = 0;
                const onLoaded = () => {
                    loaded++;
                    if (loaded === 2) {
                        const canvas1 = document.createElement('canvas');
                        canvas1.width = 16;
                        canvas1.height = 16;
                        const ctx1 = canvas1.getContext('2d');
                        ctx1.drawImage(img1, 0, 0, 16, 16);
                        const d1 = ctx1.getImageData(0, 0, 16, 16).data;

                        const canvas2 = document.createElement('canvas');
                        canvas2.width = 16;
                        canvas2.height = 16;
                        const ctx2 = canvas2.getContext('2d');
                        ctx2.drawImage(img2, 0, 0, 16, 16);
                        const d2 = ctx2.getImageData(0, 0, 16, 16).data;

                        let diff = 0;
                        for (let i = 0; i < d1.length; i += 4) {
                            diff += Math.abs(d1[i] - d2[i]);       // R
                            diff += Math.abs(d1[i+1] - d2[i+1]);   // G
                            diff += Math.abs(d1[i+2] - d2[i+2]);   // B
                        }
                        
                        const avgDiff = diff / (16 * 16 * 3);
                        // Difference threshold of pixel channels: 12 out of 255
                        resolve(avgDiff > 12);
                    }
                };
                img1.addEventListener('load', onLoaded);
                img2.addEventListener('load', onLoaded);
                img1.src = currentDataUrl;
                img2.src = prevDataUrl;
            });
        };

        const runLocalExtraction = async () => {
            if (!localVideoUrl.value) return;

            isProcessing.value = true;
            progress.value = 0;
            error.value = null;
            frames.value = [];

            // Setup a temporary video element for seekers
            const tempVideo = document.createElement('video');
            tempVideo.src = localVideoUrl.value;
            tempVideo.crossOrigin = 'anonymous';

            try {
                await new Promise((resolve, reject) => {
                    tempVideo.addEventListener('loadedmetadata', resolve, { once: true });
                    tempVideo.addEventListener('error', () => reject(new Error("Failed to load video metadata.")), { once: true });
                });

                const durationVal = tempVideo.duration;
                duration.value = durationVal;

                // Restrict to first 10 minutes (600s)
                const limitDuration = Math.min(durationVal, 600);

                let timestamps = [];
                if (samplingMode.value === 'fixed') {
                    const step = samplingInterval.value;
                    const maxTime = limitDuration + 0.1; // epsilon to handle video duration rounding/precision limits
                    for (let t = 0; t <= maxTime; t += step) {
                        timestamps.push(Math.min(t, limitDuration));
                    }
                } else {
                    // Deduplication Analysis loop
                    const step = 2.0; // sample frame every 2 seconds
                    const testTimes = [];
                    for (let t = 0; t <= limitDuration; t += step) {
                        testTimes.push(t);
                    }

                    let prevImg = null;
                    for (let i = 0; i < testTimes.length; i++) {
                        progress.value = Math.round((i / testTimes.length) * 45); // first 45% progress
                        const t = testTimes[i];
                        const img = await captureFrameAtTime(tempVideo, t);
                        const isDiff = await compareFrames(img, prevImg);
                        if (isDiff) {
                            timestamps.push(t);
                            prevImg = img;
                        }
                    }
                }

                // Limit maximum frames to prevent client crash
                if (timestamps.length > 70) {
                    timestamps = timestamps.slice(0, 70);
                }

                // Extract final frames
                const captured = [];
                for (let i = 0; i < timestamps.length; i++) {
                    const t = timestamps[i];
                    // remaining progress map (from 45% or 0% to 100%)
                    const baseProgress = samplingMode.value === 'fixed' ? 0 : 45;
                    const scaleFactor = samplingMode.value === 'fixed' ? 1.0 : 0.55;
                    progress.value = baseProgress + Math.round((i / timestamps.length) * 100 * scaleFactor);

                    const dataUrl = await captureFrameAtTime(tempVideo, t);
                    captured.push({
                        id: Date.now() + i,
                        timestamp: t,
                        image: dataUrl,
                        selected: true
                    });
                }

                frames.value = captured;
                isProcessing.value = false;
                progress.value = 100;

            } catch (err) {
                isProcessing.value = false;
                error.value = err.message || "Failed to process local video. Make sure codec is supported by your browser.";
            }
        };

        // --- Server-side URL Frame Extraction ---
        const processVideoUrl = async () => {
            if (!videoUrl.value) return;

            selectedFile.value = {
                name: videoUrl.value.length > 50 ? videoUrl.value.substring(0, 47) + '...' : videoUrl.value,
                size: 0,
                type: 'video/url'
            };

            isProcessing.value = true;
            progress.value = 0;
            error.value = null;
            frames.value = [];

            // Simulated laser progress bar matching Whisper loader
            const interval = setInterval(() => {
                if (progress.value < 96) {
                    progress.value += Math.random() * 4;
                }
            }, 600);

            try {
                const response = await axios.post('/extract-video-frames', {
                    url: videoUrl.value,
                    mode: samplingMode.value,
                    interval: samplingInterval.value
                });

                progress.value = 100;
                clearInterval(interval);

                setTimeout(() => {
                    duration.value = response.data.duration;
                    
                    // Parse returned frames
                    const loadedFrames = response.data.frames.map((f, index) => ({
                        id: Date.now() + index,
                        timestamp: f.timestamp,
                        image: f.image, // base64 encoding (e.g. data:image/jpeg;base64,...)
                        selected: true
                    }));

                    frames.value = loadedFrames;
                    
                    // Setup temporary title
                    const titleFromUrl = videoUrl.value.includes('youtube.com') || videoUrl.value.includes('youtu.be')
                        ? 'YouTube Video'
                        : videoUrl.value.split('/').pop() || 'Remote Video URL';
                    selectedFile.value = {
                        name: titleFromUrl,
                        size: 0,
                        type: 'video/url'
                    };

                    // For remote URLs, we can set localVideoUrl to null
                    // and rely on frames, or fallback to placeholder text
                    localVideoUrl.value = null; 
                    isProcessing.value = false;
                }, 800);

            } catch (err) {
                clearInterval(interval);
                isProcessing.value = false;
                selectedFile.value = null;
                error.value = err.response?.data?.error || "Failed to download and parse YouTube video URL.";
            }
        };

        // --- Custom Interval Controls ---
        const selectPreset = (preset) => {
            samplingInterval.value = preset;
            showCustomInterval.value = false;
            resampleVideo();
        };

        const toggleCustom = () => {
            if (!showCustomInterval.value) {
                showCustomInterval.value = true;
            } else {
                showCustomInterval.value = false;
                if (![1, 5, 10].includes(samplingInterval.value)) {
                    samplingInterval.value = 5; // Revert to default preset
                }
            }
        };

        const changeSamplingMode = (mode) => {
            samplingMode.value = mode;
            resampleVideo();
        };

        // --- Resample / Reset ---
        const resampleVideo = () => {
            if (selectedFile.value && selectedFile.value.type === 'video/url') {
                processVideoUrl();
            } else {
                runLocalExtraction();
            }
        };

        const resetWorkspace = () => {
            if (localVideoUrl.value) URL.revokeObjectURL(localVideoUrl.value);
            selectedFile.value = null;
            localVideoUrl.value = null;
            videoUrl.value = '';
            duration.value = 0;
            videoCurrentTime.value = 0;
            frames.value = [];
            error.value = null;
            isProcessing.value = false;
        };

        // --- Manual frame capture ---
        const captureCurrentFrame = () => {
            if (selectedFile.value && selectedFile.value.type === 'video/url') {
                return;
            }
            const video = videoPlayer.value;
            if (!video) return;

            const canvas = document.createElement('canvas');
            const scale = Math.min(1.0, 1280 / video.videoWidth);
            canvas.width = video.videoWidth * scale;
            canvas.height = video.videoHeight * scale;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

            frames.value.push({
                id: Date.now(),
                timestamp: video.currentTime,
                image: dataUrl,
                selected: true
            });

            // Re-sort slides chronologically
            frames.value.sort((a, b) => a.timestamp - b.timestamp);
            triggerAlert("Frame captured successfully!");
        };

        // --- Keyboard listener ('C' to capture) ---
        const handleKeyDown = (e) => {
            if (e.key.toLowerCase() === 'c') {
                // Ignore keypresses inside inputs
                const active = document.activeElement;
                if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
                    return;
                }
                captureCurrentFrame();
            }
        };

        // --- Timeline Seek ---
        const seekVideoFromTimeline = (e) => {
            const rect = timelineBar.value.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = Math.max(0, Math.min(100, (clickX / rect.width)));
            
            const targetTime = (percentage / 100) * duration.value;
            seekToFrame(targetTime);
        };

        const seekToFrame = (time) => {
            if (videoPlayer.value) {
                videoPlayer.value.currentTime = time;
                videoCurrentTime.value = time;
            }
        };

        const onVideoTimeUpdate = () => {
            if (videoPlayer.value) {
                videoCurrentTime.value = videoPlayer.value.currentTime;
            }
        };

        const onVideoLoadedMetadata = () => {
            if (videoPlayer.value) {
                duration.value = videoPlayer.value.duration;
            }
        };

        // --- Slide Checkbox / Actions ---
        const toggleSelectAll = (e) => {
            const checked = e.target.checked;
            frames.value.forEach(f => f.selected = checked);
        };

        const deleteFrame = (id) => {
            frames.value = frames.value.filter(f => f.id !== id);
        };

        // --- Cropper Functions ---
        const startCropping = (frame) => {
            croppingFrame.value = frame;
            nextTick(() => {
                if (cropperImage.value) {
                    cropperInstance = new Cropper(cropperImage.value, {
                        viewMode: 1,
                        dragMode: 'move',
                        aspectRatio: NaN, // free cropping
                        autoCropArea: 0.9,
                        restore: false,
                        guides: true,
                        center: true,
                        highlight: false,
                        cropBoxMovable: true,
                        cropBoxResizable: true,
                        toggleDragModeOnDblclick: false
                    });
                }
            });
        };

        const applyCrop = () => {
            if (cropperInstance && croppingFrame.value) {
                const canvas = cropperInstance.getCroppedCanvas();
                if (canvas) {
                    croppingFrame.value.image = canvas.toDataURL('image/jpeg', 0.85);
                    triggerAlert("Frame cropped successfully!");
                }
            }
            closeCropper();
        };

        const closeCropper = () => {
            if (cropperInstance) {
                cropperInstance.destroy();
                cropperInstance = null;
            }
            croppingFrame.value = null;
        };

        // --- PDF Exporter (jsPDF) ---
        const compileAndDownloadPdf = async () => {
            const selected = frames.value.filter(f => f.selected);
            if (selected.length === 0) {
                error.value = "Cannot compile PDF. Please select at least one frame.";
                showDownloadMenu.value = false;
                return;
            }

            isProcessing.value = true;
            progress.value = 0;
            showDownloadMenu.value = false;

            try {
                // Helper to load image dimensions asynchronously
                const getImageDimensions = (dataUrl) => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            resolve({ width: img.naturalWidth || 800, height: img.naturalHeight || 450 });
                        };
                        img.onerror = () => {
                            resolve({ width: 800, height: 450 });
                        };
                        img.src = dataUrl;
                    });
                };

                // Get dimensions for all selected frames to match aspect ratio
                const frameDimensions = [];
                for (let i = 0; i < selected.length; i++) {
                    const dims = await getImageDimensions(selected[i].image);
                    frameDimensions.push(dims);
                }

                let pdf;
                const layout = pdfSettings.value.layout;

                if (layout === 'original') {
                    // Borderless page matching the exact aspect ratio & resolution of the frame
                    const firstDim = frameDimensions[0] || { width: 800, height: 450 };
                    pdf = new jsPDF({
                        orientation: firstDim.width > firstDim.height ? 'l' : 'p',
                        unit: 'px',
                        format: [firstDim.width, firstDim.height],
                        compress: true
                    });

                    for (let i = 0; i < selected.length; i++) {
                        progress.value = Math.round((i / selected.length) * 100);
                        const frame = selected[i];
                        const dim = frameDimensions[i] || { width: 800, height: 450 };

                        if (i > 0) {
                            const pageOrientation = dim.width > dim.height ? 'l' : 'p';
                            pdf.addPage([dim.width, dim.height], pageOrientation);
                        }

                        // Draw image border-to-border (100% full screen page match, no margins)
                        pdf.addImage(frame.image, 'JPEG', 0, 0, dim.width, dim.height, undefined, 'FAST');
                    }
                } else {
                    // A4 layouts (Landscape, Portrait Grid, Portrait Notes)
                    const orientation = layout === 'landscape' ? 'landscape' : 'portrait';
                    pdf = new jsPDF({
                        orientation: orientation,
                        unit: 'mm',
                        format: 'a4',
                        compress: true
                    });

                    const pageWidth = pdf.internal.pageSize.getWidth();
                    const pageHeight = pdf.internal.pageSize.getHeight();
                    let pdfPageIdx = 1;

                    // Helper to draw header / footer
                    const drawPageDecorations = (pageIndex, totalPages, frameTimestamp) => {
                        pdf.setFont('helvetica', 'normal');
                        
                        // Draw Header (Doc Title)
                        if (pdfSettings.value.title) {
                            pdf.setFontSize(8);
                            pdf.setTextColor(150, 150, 150);
                            pdf.text(pdfSettings.value.title.toUpperCase(), 15, 10);
                            pdf.setDrawColor(230, 230, 230);
                            pdf.line(15, 12, pageWidth - 15, 12);
                        }

                        // Draw Footer (Page & timestamp metadata)
                        pdf.setDrawColor(240, 240, 240);
                        pdf.line(15, pageHeight - 12, pageWidth - 15, pageHeight - 12);

                        pdf.setFontSize(8);
                        pdf.setTextColor(160, 160, 160);
                        
                        if (pdfSettings.value.pageNumbers) {
                            const pageStr = `Page ${pageIndex} of ${totalPages}`;
                            pdf.text(pageStr, pageWidth - 15 - pdf.getTextWidth(pageStr), pageHeight - 8);
                        }

                        if (pdfSettings.value.timestamps && frameTimestamp !== null) {
                            pdf.text(`Video Time: ${formatTime(frameTimestamp)}`, 15, pageHeight - 8);
                        }
                    };

                    // --- 1. COVER PAGE ---
                    if (pdfSettings.value.coverPage) {
                        pdf.setFillColor(255, 241, 242); // Rose pale
                        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
                        
                        // Cover border
                        pdf.setDrawColor(244, 63, 94); // Logo blue/rose
                        pdf.setLineWidth(1.5);
                        pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);

                        // Title
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(28);
                        pdf.setTextColor(15, 23, 42); // slate 900
                        pdf.text(pdfSettings.value.title || "Video Slides", pageWidth / 2, pageHeight / 2 - 20, { align: 'center' });

                        // Subtitle
                        pdf.setFont('helvetica', 'normal');
                        pdf.setFontSize(12);
                        pdf.setTextColor(100, 116, 139);
                        const subtitle = `Extracted from video presentation (${selected.length} slides total)`;
                        pdf.text(subtitle, pageWidth / 2, pageHeight / 2 - 5, { align: 'center' });

                        // Date & Brand Footer
                        pdf.setFontSize(10);
                        pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
                        
                        pdf.setFont('helvetica', 'bold');
                        pdf.setTextColor(244, 63, 94);
                        pdf.text("VideoTranscribe.ai", pageWidth / 2, pageHeight - 22, { align: 'center' });
                        
                        pdf.addPage();
                        pdfPageIdx++;
                    }

                    // --- 2. LAYOUTS ---
                    if (layout === 'landscape') {
                        // Single Slide per Page - Landscape orientation
                        const totalPages = selected.length + (pdfSettings.value.coverPage ? 1 : 0);

                        for (let i = 0; i < selected.length; i++) {
                            progress.value = Math.round((i / selected.length) * 100);

                            if (i > 0) pdf.addPage();
                            
                            const frame = selected[i];
                            const dim = frameDimensions[i] || { width: 800, height: 450 };
                            const imageAspect = dim.width / dim.height;

                            // Target dimensions inside margins
                            const margin = 15;
                            const w = pageWidth - (margin * 2);
                            
                            // Dynamic height matching aspect ratio to prevent squishing/distortion
                            let h = w / imageAspect;
                            const maxH = pageHeight - (margin * 2) - 10;
                            if (h > maxH) {
                                h = maxH;
                            }

                            const finalW = h * imageAspect;
                            const x = margin + (w - finalW) / 2; // Center horizontally
                            const y = margin + (maxH - h) / 2 + 5; // Center vertically

                            pdf.addImage(frame.image, 'JPEG', x, y, finalW, h, undefined, 'FAST');
                            drawPageDecorations(pdfPageIdx + i, totalPages, frame.timestamp);
                        }
                    } 
                    else if (layout === 'portrait_grid') {
                        // Two Slides per Page - Portrait orientation (stacked)
                        const slidesPerPage = 2;
                        const totalDocPages = Math.ceil(selected.length / slidesPerPage) + (pdfSettings.value.coverPage ? 1 : 0);
                        let currentDocPage = pdfSettings.value.coverPage ? 2 : 1;

                        for (let i = 0; i < selected.length; i += slidesPerPage) {
                            progress.value = Math.round((i / selected.length) * 100);

                            if (i > 0) pdf.addPage();

                            const margin = 15;
                            const w = pageWidth - (margin * 2); // 180mm

                            // First Frame
                            const frame1 = selected[i];
                            const dim1 = frameDimensions[i] || { width: 800, height: 450 };
                            const aspect1 = dim1.width / dim1.height;
                            const h1 = w / aspect1;
                            pdf.addImage(frame1.image, 'JPEG', margin, 20, w, h1, undefined, 'FAST');
                            drawPageDecorations(currentDocPage, totalDocPages, frame1.timestamp);

                            // Second Frame (if exists)
                            if (i + 1 < selected.length) {
                                const frame2 = selected[i + 1];
                                const dim2 = frameDimensions[i + 1] || { width: 800, height: 450 };
                                const aspect2 = dim2.width / dim2.height;
                                const h2 = w / aspect2;
                                pdf.addImage(frame2.image, 'JPEG', margin, 150, w, h2, undefined, 'FAST');
                                // Draw a small line divider between them
                                pdf.setDrawColor(240, 240, 240);
                                pdf.setLineWidth(0.5);
                                pdf.line(margin, 135, pageWidth - margin, 135);
                                
                                if (pdfSettings.value.timestamps) {
                                    pdf.setFontSize(8);
                                    pdf.setTextColor(160, 160, 160);
                                    pdf.text(`Video Time: ${formatTime(frame2.timestamp)}`, margin, 255);
                                }
                            }

                            currentDocPage++;
                        }
                    } 
                    else if (layout === 'portrait_notes') {
                        // One Slide on Top half + Lined Notes on bottom half
                        const totalPages = selected.length + (pdfSettings.value.coverPage ? 1 : 0);

                        for (let i = 0; i < selected.length; i++) {
                            progress.value = Math.round((i / selected.length) * 100);

                            if (i > 0) pdf.addPage();

                            const frame = selected[i];
                            const dim = frameDimensions[i] || { width: 800, height: 450 };
                            const imageAspect = dim.width / dim.height;

                            const margin = 15;
                            const w = pageWidth - (margin * 2); // 180mm
                            const h = w / imageAspect;

                            // Draw slide
                            pdf.addImage(frame.image, 'JPEG', margin, 20, w, h, undefined, 'FAST');

                            // Draw Notes Section Lines
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(10);
                            pdf.setTextColor(15, 23, 42);
                            pdf.text("NOTES", margin, 140);

                            pdf.setDrawColor(220, 225, 230);
                            pdf.setLineWidth(0.3);
                            const linesCount = 7;
                            const lineSpacing = 15;
                            const startY = 150;

                            for (let line = 0; line < linesCount; line++) {
                                const yPos = startY + (line * lineSpacing);
                                pdf.line(margin, yPos, pageWidth - margin, yPos);
                            }

                            drawPageDecorations(pdfPageIdx + i, totalPages, frame.timestamp);
                        }
                    }
                }

                // Save PDF
                const sanitizedTitle = (pdfSettings.value.title || 'video_slides')
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '_')
                    .substring(0, 30);
                
                pdf.save(`${sanitizedTitle}_${Date.now()}.pdf`);
                isProcessing.value = false;
                triggerAlert("PDF downloaded successfully!");

            } catch (err) {
                console.error(err);
                isProcessing.value = false;
                error.value = "Failed to generate PDF. Make sure your browser has enough memory.";
            }
        };

        // --- Alert popups ---
        const triggerAlert = (message) => {
            alertMessage.value = message;
            setTimeout(() => {
                alertMessage.value = null;
            }, 3000);
        };

        const shareDocument = () => {
            const pageUrl = window.location.href;
            navigator.clipboard.writeText(pageUrl);
            triggerAlert("Page link copied to clipboard!");
        };

        // --- Hooks ---
        onMounted(() => {
            window.addEventListener('keydown', handleKeyDown);
        });

        onUnmounted(() => {
            window.removeEventListener('keydown', handleKeyDown);
            if (localVideoUrl.value) URL.revokeObjectURL(localVideoUrl.value);
        });

        return {
            videoUrl, selectedFile, localVideoUrl, duration, videoCurrentTime,
            isProcessing, progress, dragActive, error, alertMessage,
            frames, samplingMode, samplingInterval, showCustomInterval,
            showDownloadMenu, croppingFrame, cropperImage, pdfSettings,
            videoPlayer, timelineBar,
            isAllSelected, selectedCount, timelineTicks, youtubeEmbedUrl,
            formatTime, getPercentage, handleFileSelect, handleDrop,
            processVideoUrl, resampleVideo, resetWorkspace,
            captureCurrentFrame, seekVideoFromTimeline, seekToFrame,
            onVideoTimeUpdate, onVideoLoadedMetadata, toggleSelectAll,
            deleteFrame, startCropping, applyCrop, closeCropper,
            compileAndDownloadPdf, shareDocument,
            selectPreset, toggleCustom, changeSamplingMode
        };
    }
};
</script>

<style scoped>
@keyframes scan {
    0% { transform: translateY(0); opacity: 0; }
    50% { opacity: 1; }
    100% { transform: translateY(220px); opacity: 0; }
}

.animate-scan {
    animation: scan 2.5s infinite linear;
}

@keyframes shimmer {
    0% { transform: translateX(-150%); }
    100% { transform: translateX(150%); }
}

kbd {
    box-shadow: 0 1px 0 rgba(0,0,0,0.1), inset 0 0 0 1px #fff;
}

/* Custom range slider styling */
.custom-range-slider {
    -webkit-appearance: none;
    width: 100%;
    height: 5px;
    background: #e2e8f0; /* slate 200 */
    border-radius: 9999px;
    outline: none;
    transition: background 0.3s;
}

.custom-range-slider::-webkit-slider-runnable-track {
    width: 100%;
    height: 5px;
    background: #e2e8f0;
    border-radius: 9999px;
}

.custom-range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #f43f5e; /* logo-blue / rose 500 */
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(244, 63, 94, 0.4);
    margin-top: -5px; /* Centers the thumb vertically on the track */
    transition: transform 0.1s, background-color 0.2s;
}

.custom-range-slider::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    background: #e11d48; /* darker rose */
}

.custom-range-slider::-moz-range-track {
    width: 100%;
    height: 5px;
    background: #e2e8f0;
    border-radius: 9999px;
}

.custom-range-slider::-moz-range-thumb {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #f43f5e;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(244, 63, 94, 0.4);
    transition: transform 0.1s, background-color 0.2s;
}

.custom-range-slider::-moz-range-thumb:hover {
    transform: scale(1.2);
    background: #e11d48;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
    width: 6px;
}
::-webkit-scrollbar-track {
    background: transparent;
}
::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1;
}
</style>
